/* eslint-disable consistent-return */
/* eslint-disable no-param-reassign */
import React from 'react';
import { Icon } from 'semantic-ui-react';
import _ from 'lodash';
import constants from './constants/constants';
import {
  createConversionAdsetObject,
  createDAAAdsetObject,
  createBreakdownCarouselAd,
  createCanvasAd,
  createCollectionAd,
  createDAAAd,
  createStillAd,
  createVideoAd
} from './PackageLauncher';
import { buildAdSet } from './CampaignBuilder';
import { getAudiences } from '../../fbApi/Audience';
import { getProductSets } from '../../fbApi/ProductCatalog';

const { LAUNCHER } = constants;
const {
  CAROUSEL,
  CANVAS,
  COLLECTION,
  DAA,
  CINEMAGRAPH,
  VIDEO,
  STILL,
  CANVAS_VALUE,
  COLLECTION_VALUE,
  CAROUSEL_VALUE,
  CINEMAGRAPH_VALUE,
  VIDEO_VALUE,
  STILL_VALUE
} = LAUNCHER;

/**
 * type - The type of the launching object. ex) Campaign, Adset, Ad
 * id - this will be the id of the object. It depends on the type.
 */
export const returnUpdatedLaunchingStatus = (
  launchingStatus,
  type,
  id,
  status,
  errorMessage = null,
  facebookObjectId,
  checkedAdIds
) => {
  let campaigns = [...launchingStatus]; // launchingStatus is list of campaigns

  if (type === 'Campaign') {
    campaigns = campaigns.map((campaign) => {
      const { id: campaignId } = campaign;

      if (campaignId === id) {
        return {
          ...campaign,
          status,
          errorMessage,
          facebookCampaignId: facebookObjectId
        };
      }
      return {
        ...campaign
      };
    });
  } else if (type === 'Adset') {
    campaigns = campaigns.map((campaign) => {
      const { adsets } = campaign;

      return {
        ...campaign,
        adsets: adsets.map((adset) => {
          const { id: adsetId } = adset;

          if (adsetId === id) {
            return {
              ...adset,
              status,
              errorMessage,
              facebookAdsetId: facebookObjectId
            };
          }
          return {
            ...adset
          };
        })
      };
    });
  } else {
    campaigns = campaigns.map((campaign) => {
      const { adsets } = campaign;

      return {
        ...campaign,
        adsets: adsets.map((adset) => {
          const { ads, name: adsetName } = adset;

          const checkedAds = ads.filter((ad) =>
            checkedAdIds.find((checkedId) =>
              // eslint-disable-next-line no-nested-ternary
              adsetName.includes('_Lookalike')
                ? ad.id === checkedId || ad.id === checkedId * 2 + 1
                : adsetName.includes('_Retargeting')
                ? ad.id === checkedId || ad.id === checkedId * 2
                : ad.id === checkedId
            )
          );

          return {
            ...adset,
            ads: checkedAds.map((ad) => {
              const { id: adId } = ad;

              if (adId === id) {
                return {
                  ...ad,
                  status,
                  errorMessage,
                  facebookAdId: facebookObjectId
                };
              }
              return {
                ...ad
              };
            })
          };
        })
      };
    });
  }

  return campaigns;
};

export const launchAdsetsForCampaign = async (
  state,
  props,
  campaign,
  newCampaignId,
  updateLaunchingStatusCallback = null,
  packageLaunchAdSectionComponent = null
) => {
  const { packageToLaunch } = props;
  const {
    locationType,
    token,
    zipCodesToInclude,
    zipCodesToExclude,
    includeTargetingSelected,
    excludeTargetingSelected,
    form
  } = state;

  const parameters = {
    locationType,
    token,
    zipCodesToInclude,
    zipCodesToExclude,
    includeTargetingSelected,
    excludeTargetingSelected
  };

  let adsetsToLaunch = packageToLaunch.adsets.filter(
    (item) => item.campaignId === campaign.id
  );

  if (state.includeLookalikes) {
    const duplicateAdsets = adsetsToLaunch
      .filter((adset) => !adset.daaType)
      .map((adset) => {
        const retargetNameSplit = adset.adSetName.split('_');
        const lookalikeNameSplit = adset.adSetName.split('_');

        retargetNameSplit[4] = 'Retargeting';
        lookalikeNameSplit[4] = 'Lookalike';

        const retargetAdsetName = retargetNameSplit.join('_');
        const lookalikeAdsetName = lookalikeNameSplit.join('_');

        return [
          {
            ...adset,
            id: adset.id * 2,
            adSetName: retargetAdsetName
          },
          {
            ...adset,
            id: adset.id * 2 + 1,
            adSetName: lookalikeAdsetName
          }
        ];
      })
      .flat();
    adsetsToLaunch = [...adsetsToLaunch.concat(duplicateAdsets)];
  }

  const launchAdsetResult = await Promise.all(
    adsetsToLaunch.map(async (adset) => {
      const { id: adsetId, daaType } = adset;
      if (updateLaunchingStatusCallback) {
        updateLaunchingStatusCallback('Adset', adsetId, 'Launching', '', null);
      }

      const adSetData = !daaType
        ? createConversionAdsetObject(
            adset,
            campaign,
            newCampaignId,
            form,
            parameters
          )
        : await createDAAAdsetObject(
            adset,
            campaign,
            newCampaignId,
            form,
            parameters
          );

      const resultAdSet = await buildAdSet(adSetData, token);

      let status = '';
      let errorMessage = null;

      if (resultAdSet.id) {
        status = 'Succeed';
      } else {
        status = 'Failed';
        errorMessage = resultAdSet.errorMessage;
      }

      if (updateLaunchingStatusCallback) {
        updateLaunchingStatusCallback(
          'Adset',
          adsetId,
          status,
          errorMessage,
          resultAdSet.id || null
        );
      }

      return Promise.resolve({ ...resultAdSet, packageAdsetObj: adset });
    })
  );

  await Promise.all(
    launchAdsetResult.map(async (adsetResult) => {
      const { packageAdsetObj, id } = adsetResult;
      const launchResult = await launchAdsForAdSet(
        state,
        props,
        packageLaunchAdSectionComponent,
        campaign,
        packageAdsetObj,
        id,
        updateLaunchingStatusCallback
      );
      return Promise.resolve(launchResult);
    })
  );
};

// Launch ads to Facebook for an adset according to the type
export const launchAdsForAdSet = async (
  state,
  props,
  packageLaunchAdSectionComponent,
  campaign,
  adset,
  newAdSetId,
  updateLaunchingStatusCallback
) => {
  try {
    const { form, checkedAdIds, includeLookalikes } = state;
    const { packageToLaunch, utmProfiles } = props;

    const { adSetName } = adset;
    const adsetIsLookalike = adSetName.includes('_Lookalike');
    const adsetIsRetargeting = adSetName.includes('_Retargeting');

    const adsetIsDuplicate =
      includeLookalikes && (adsetIsLookalike || adsetIsRetargeting);

    if (form.utmOverrideSelected) {
      const utmProfile = utmProfiles.find(
        (item) => item.id === form.utmOverrideSelected
      );
      form.utmProfile = utmProfile;
    }
    let formCardThumbnails = [];
    if (packageLaunchAdSectionComponent) {
      const { state: childState } = packageLaunchAdSectionComponent;
      formCardThumbnails = childState.cardThumbnails;
    }
    if (formCardThumbnails && formCardThumbnails.length > 0) {
      form.formCardThumbnails = formCardThumbnails;
    }

    let adsToLaunch = packageToLaunch.ads.filter((item) => {
      let matchingAdCondition =
        item.adSetId === adset.id && checkedAdIds.find((id) => item.id === id);

      if (adsetIsDuplicate) {
        // eslint-disable-next-line no-nested-ternary
        matchingAdCondition = adsetIsLookalike
          ? adset.id === item.adSetId || adset.id === item.adSetId * 2 + 1
          : adsetIsRetargeting
          ? adset.id === item.adSetId || adset.id === item.adSetId * 2
          : adset.id === item.adSetId;
      }
      return matchingAdCondition;
    });

    if (adsetIsDuplicate) {
      adsToLaunch = adsToLaunch.map((ad) => {
        const adNameSplit = ad.name.split('_');
        const splitLength = adNameSplit.length;
        const newAdName = `${adSetName}_${adNameSplit
          .slice(splitLength - 2, splitLength)
          .join('_')}`;
        return {
          ...ad,
          name: newAdName,
          id: adsetIsLookalike ? ad.id * 2 + 1 : ad.id * 2,
          adSetId: adsetIsLookalike ? ad.adSetId * 2 + 1 : ad.adSetId * 2
        };
      });
    }

    const launchAdsFromAdsetResult = await Promise.all(
      adsToLaunch
        .filter((ad) => ad && ad.ad)
        .map(async (ad) => {
          if (!newAdSetId) {
            updateLaunchingStatusCallback(
              'Ad',
              ad.id,
              'Failed',
              'The adset launch failed, so the ad could not be launched',
              null
            );
            return;
          }
          const launchAdFromAdsetPromise = await launchAdFromAdset(
            state,
            campaign,
            adset,
            ad,
            newAdSetId,
            packageLaunchAdSectionComponent,
            updateLaunchingStatusCallback
          );
          return Promise.resolve(launchAdFromAdsetPromise);
        })
    );

    return Promise.resolve(launchAdsFromAdsetResult);
  } catch (error) {
    console.log('Something went wrong in launchAdsForAdSet(): ', error);
    return Promise.resolve([]);
  }
};

export const launchAdFromAdset = async (
  state,
  campaign,
  adset,
  ad,
  newAdSetId,
  packageLaunchAdSectionComponent,
  updateLaunchingStatusCallback
) => {
  try {
    const { id: adId, ad: innerAd } = ad;
    const { id: innerAdId, type: adType } = innerAd;
    const {
      checkedAdIds,
      token,
      form,
      includeLookalikes,
      idsOfCollectionAdsToConvert
    } = state;

    const matchingFormAdset = form.adsets.find(
      (formAdset) => formAdset.id === ad.adSetId
    );

    if (updateLaunchingStatusCallback) {
      updateLaunchingStatusCallback('Ad', adId, 'Launching', '', null);
    }

    if (!innerAd && updateLaunchingStatusCallback) {
      updateLaunchingStatusCallback(
        'Ad',
        adId,
        'Failed',
        'The inner ad (prototype) object needed is not defined',
        null
      );
      return;
    }

    const nameToUse =
      (matchingFormAdset && matchingFormAdset.name) || adset.adSetName;

    const adsetType = nameToUse.split('_')[4];

    const foundCheckedAdId = checkedAdIds.find((id) => {
      if (includeLookalikes) {
        switch (adsetType) {
          case 'Lookalike':
            return ad.id === id || ad.id === id * 2 + 1;
          case 'Retargeting':
            return ad.id === id || ad.id === id * 2;
          default:
            return ad.id === id;
        }
      }
      return ad.id === id;
    });

    if (!foundCheckedAdId && updateLaunchingStatusCallback) {
      updateLaunchingStatusCallback(
        'Ad',
        adId,
        'Failed',
        'The ad was not selected for launch.',
        null
      );
      return;
    }

    if (adType === 'Collection') {
      const foundId = idsOfCollectionAdsToConvert.find((id) => id === adId);
      if (foundId) {
        innerAd.convertToCinemagraph = true;
      }
    }

    const splitAdsetName = adset.adSetName.split('_');
    const splitAdName = ad.name.split('_');
    const adsetVariant = splitAdsetName[4]; // ex: In Market, Lookalike, Retargeting
    const adVariant = splitAdName[4]; // ex: In Market, Lookalike, Retargeting

    // On relaunches, sometimes lookalike and retargeting duplicates are not named properly
    if (
      (adsetVariant === 'Lookalike' || adsetVariant === 'Retargeting') &&
      adVariant !== adsetVariant
    ) {
      ad.name = splitAdName
        .slice(0, 4)
        .concat(adsetVariant)
        .concat(splitAdName.slice(5));
    }

    let resultAd = {};
    switch (adType) {
      case CANVAS:
        resultAd = await createCanvasAd(
          ad,
          campaign,
          newAdSetId,
          adset,
          token,
          form
        );
        break;
      case COLLECTION:
        if (packageLaunchAdSectionComponent) {
          const { state: childState } = packageLaunchAdSectionComponent;
          const { currentSingleThumbnails } = childState;
          if (currentSingleThumbnails) {
            const foundThumbnailObj = currentSingleThumbnails.find(
              (thumbnailObj) => {
                return thumbnailObj.id === innerAdId;
              }
            );
            if (foundThumbnailObj) {
              ad.ad.thumbnail = foundThumbnailObj.thumbnail;
            }
          }
        }
        resultAd = !innerAd.convertToCinemagraph
          ? await createCollectionAd(
              ad,
              campaign,
              newAdSetId,
              adset,
              form,
              token
            )
          : (resultAd = await createVideoAd(
              ad,
              campaign,
              newAdSetId,
              adset,
              token,
              form
            ));
        break;
      case CAROUSEL:
        resultAd = await createBreakdownCarouselAd(
          ad,
          campaign,
          newAdSetId,
          adset,
          form,
          token
        );
        break;
      case DAA:
        resultAd = await createDAAAd(
          ad,
          campaign,
          newAdSetId,
          adset,
          form,
          token
        );
        break;
      case CINEMAGRAPH:
      case VIDEO:
        if (packageLaunchAdSectionComponent) {
          const { state: childState } = packageLaunchAdSectionComponent;
          const { currentSingleThumbnails } = childState;
          if (currentSingleThumbnails) {
            const foundThumbnailObj = currentSingleThumbnails.find(
              (thumbnailObj) => {
                return thumbnailObj.id === innerAdId;
              }
            );
            if (foundThumbnailObj) {
              ad.ad.thumbnail = foundThumbnailObj.thumbnail;
            }
          }
        }
        resultAd = await createVideoAd(
          ad,
          campaign,
          newAdSetId,
          adset,
          token,
          form
        );
        break;
      case STILL:
        resultAd = await createStillAd(
          ad,
          campaign,
          newAdSetId,
          adset,
          token,
          form
        );
        break;
      default:
        break;
    }

    let status = '';
    let errorMessage = null;

    if (resultAd.id) {
      status = 'Succeed';
    } else {
      status = 'Failed';
      const { error } = resultAd;
      const {
        error_user_title: errorUserTitle,
        error_user_msg: errorUserMsg,
        message
      } = error || {};
      errorMessage =
        errorUserTitle && errorUserMsg
          ? `${errorUserTitle} ${errorUserMsg}`
          : `${message}`;
    }

    if (updateLaunchingStatusCallback) {
      updateLaunchingStatusCallback(
        'Ad',
        adId,
        status,
        errorMessage,
        resultAd.id || null
      );
    }

    return Promise.resolve(resultAd);
  } catch (error) {
    const { id: adId } = ad;
    const status = 'Failed';
    const errorMessage =
      error.message ||
      'Something went wrong. Check if the ad prototype is valid.';
    if (updateLaunchingStatusCallback) {
      updateLaunchingStatusCallback('Ad', adId, status, errorMessage, null);
    }
    console.log('Something went wrong in launchAdFromAdset()', error);
    return Promise.resolve({});
  }
};

export const returnFailedCollectionAdStatuses = (relaunchData) => {
  try {
    const filteredRelaunchData = relaunchData.map((relaunchObj) => {
      const { adsFromPackage, adsetStatuses } = relaunchObj;
      const adsetStatusesWithFailedCollections = adsetStatuses.map(
        (adsetStatus) => {
          const { ads: adStatuses, name } = adsetStatus;
          const adsetType = name.split('_')[4];
          const failedCollectionAdStatuses = adStatuses
            .filter((adStatus) => {
              const foundAdInPackage = adsFromPackage.find((ad) => {
                switch (adsetType) {
                  case 'Lookalike':
                    return (
                      adStatus.id === ad.id || adStatus.id === ad.id * 2 + 1
                    );
                  case 'Retargeting':
                    return adStatus.id === ad.id || adStatus.id === ad.id * 2;
                  default:
                    return adStatus.id === ad.id;
                }
              });

              return (
                foundAdInPackage &&
                foundAdInPackage.ad.type === 'Collection' &&
                adStatus.status === 'Failed'
              );
            })
            .map((adStatus) => adStatus);

          return _.flatten(failedCollectionAdStatuses);
        }
      );

      return _.flatten(adsetStatusesWithFailedCollections);
    });

    return _.flatten(filteredRelaunchData);
  } catch (error) {
    console.log('Error in returnFailedCollectionAdStatuses():', error);
    return [];
  }
};

export const returnRelaunchData = (
  launchingStatusCampaigns,
  packageToLaunch
) => {
  try {
    const {
      campaigns: packageCampaigns,
      adsets: packageAdsets,
      ads: packageAds
    } = packageToLaunch;
    const relaunchData = [];

    launchingStatusCampaigns.forEach((statusCampaign) => {
      const campaignFromPackage = packageCampaigns.find(
        (campaign) => campaign.id === statusCampaign.id
      );
      const { adsets: adsetStatuses } = statusCampaign;

      const adsetStatusesWithFailedAds = adsetStatuses.filter(
        (adsetStatusObject) => {
          const { ads } = adsetStatusObject;
          const failedAds = ads.filter((ad) => ad.status !== 'Succeed');
          return failedAds.length > 0;
        }
      );

      const adsetsFromPackage =
        adsetStatusesWithFailedAds.length > 0
          ? adsetStatusesWithFailedAds
              .map((adsetStatusObject) => {
                const adsetType = adsetStatusObject.name.split('_')[4];
                return packageAdsets.find((packageAdset) => {
                  switch (adsetType) {
                    case 'Lookalike':
                      return adsetStatusObject.id === packageAdset.id * 2 + 1;
                    case 'Retargeting':
                      return adsetStatusObject.id === packageAdset.id * 2;
                    default:
                      return adsetStatusObject.id === packageAdset.id;
                  }
                });
              })
              .filter((adsetObj) => adsetObj)
          : [];

      const adsFromPackage =
        adsetStatusesWithFailedAds.length > 0
          ? adsetStatusesWithFailedAds.map((adsetStatusObject) => {
              const adsetType = adsetStatusObject.name.split('_')[4];
              return packageAds.filter((packageAd) => {
                switch (adsetType) {
                  case 'Lookalike':
                    return (
                      adsetStatusObject.id === packageAd.adSetId ||
                      adsetStatusObject.id === packageAd.adSetId * 2 + 1
                    );
                  case 'Retargeting':
                    return (
                      adsetStatusObject.id === packageAd.adSetId ||
                      adsetStatusObject.id === packageAd.adSetId * 2
                    );
                  default:
                    return (
                      adsetStatusObject.id === packageAd.adSetId ||
                      adsetStatusObject.id === packageAd.adSetId
                    );
                }
              });
            })
          : [];

      if (adsetsFromPackage.length > 0) {
        const relaunchDataObject = {
          campaignFromPackage,
          adsetsFromPackage,
          adsFromPackage: adsFromPackage.flat(),
          adsetStatuses: adsetStatusesWithFailedAds
        };
        relaunchData.push(relaunchDataObject);
      }
    });

    return relaunchData;
  } catch (error) {
    console.log('Something went wrong in setupForRelaunch:', error);

    return {};
  }
};

export const getInitRelaunchStatuses = (state) => {
  const { relaunchData, launchingStatus, idsOfCollectionAdsToConvert } = state;
  let failedAdStatusesFromAllCampaigns = [];
  relaunchData.forEach((relaunchDataObject) => {
    const { adsetStatuses } = relaunchDataObject;
    adsetStatuses.forEach((adsetStatusObject) => {
      const { ads } = adsetStatusObject;
      const failedAdStatuses =
        ads.length > 0 ? ads.filter((ad) => ad.status !== 'Succeed') : [];
      if (failedAdStatuses.length > 0) {
        failedAdStatuses.forEach((status) => {
          const foundId = idsOfCollectionAdsToConvert.find(
            (id) => status.id === id
          );
          if (foundId) {
            status.name = status.name.replace('Collection', 'Cinemagraph');
          }
        });
        failedAdStatusesFromAllCampaigns.push(failedAdStatuses);
      }
    });
  });
  failedAdStatusesFromAllCampaigns = failedAdStatusesFromAllCampaigns.flat();
  const initialRelaunchStatuses = initRelaunchStatuses(
    launchingStatus,
    failedAdStatusesFromAllCampaigns,
    null
  );
  return initialRelaunchStatuses;
};

const initRelaunchStatuses = (
  launchingStatus,
  failedAdStatuses,
  updateRelaunchingAdStatusesCallback
) => {
  if (failedAdStatuses.length > 0) {
    const updatedRelaunchStatuses = returnUpdatedRelaunchingAdStatuses(
      launchingStatus,
      failedAdStatuses,
      'Launching',
      ''
    );
    if (updateRelaunchingAdStatusesCallback) {
      updateRelaunchingAdStatusesCallback(updatedRelaunchStatuses);
    }
    return updatedRelaunchStatuses;
  }
};

const returnUpdatedRelaunchingAdStatuses = (
  launchingStatus,
  failedAdStatuses,
  status,
  errorMessage = null
) => {
  let campaigns = [...launchingStatus]; // launchingStatus is list of campaigns
  failedAdStatuses.forEach((adStatus) => {
    const { id, facebookObjectId } = adStatus;
    campaigns = campaigns.map((campaign) => {
      const { adsets } = campaign;

      return {
        ...campaign,
        adsets: adsets.map((adset) => {
          const { ads, name } = adset;
          const adsetType = name.split('_')[4];
          return {
            ...adset,
            ads: ads.map((ad) => {
              const { id: adId } = ad;
              let idToCheck = id;
              switch (adsetType) {
                case 'Lookalike':
                  idToCheck = adId * 2 + 1;
                  break;
                case 'Retargeting':
                  idToCheck = adId * 2;
                  break;
                default:
                  break;
              }

              if (adId === id || idToCheck === id) {
                return {
                  ...ad,
                  status,
                  errorMessage,
                  facebookAdId: facebookObjectId
                };
              }
              return {
                ...ad
              };
            })
          };
        })
      };
    });
  });

  return campaigns;
};

export const relaunchFailedAdsInPackage = async (
  state,
  packageLaunchAdSectionComponent,
  updateLaunchingStatusCallback
) => {
  const { relaunchData } = state;
  const relaunchAdsForAdsetFromCampaignsPromises = relaunchData.map(
    async (relaunchDataObject) => {
      const {
        campaignFromPackage,
        adsetsFromPackage,
        adsFromPackage,
        adsetStatuses
      } = relaunchDataObject;
      try {
        const relaunchFailedAdsForAdsetPromises = adsetStatuses.map(
          async (adsetStatusObject) => {
            if (!adsetStatusObject) {
              return Promise.resolve([]);
            }
            const {
              ads,
              facebookAdsetId,
              id: statusObjId,
              name: statusObjName
            } = adsetStatusObject;
            const adsetType = statusObjName.split('_')[4];

            const adsetFromPackage = adsetsFromPackage.find((adset) => {
              const adsetId = adset.id;
              let adsetIdToCheck = adsetId;
              switch (adsetType) {
                case 'Lookalike':
                  adsetIdToCheck = adsetId * 2 + 1;
                  break;
                case 'Retargeting':
                  adsetIdToCheck = adsetId * 2;
                  break;
                default:
                  break;
              }
              return adsetIdToCheck === statusObjId;
            });
            const packageAdsForAdset = adsFromPackage.filter((packageAd) => {
              let adsetIdToCheck = packageAd.adSetId;
              switch (adsetType) {
                case 'Lookalike':
                  adsetIdToCheck = packageAd.adSetId * 2 + 1;
                  break;
                case 'Retargeting':
                  adsetIdToCheck = packageAd.adSetId * 2;
                  break;
                default:
                  break;
              }
              return adsetIdToCheck === statusObjId;
            });
            const failedAdStatuses = ads.filter(
              (ad) => ad.status !== 'Succeed'
            );
            const failedPackageAdsFromAdset = failedAdStatuses.map(
              (failedAd) => {
                return packageAdsForAdset.find((ad) => {
                  let adIdToCheck = ad.id;
                  switch (adsetType) {
                    case 'Lookalike':
                      adIdToCheck = ad.id * 2 + 1;
                      break;
                    case 'Retargeting':
                      adIdToCheck = ad.id * 2;
                      break;
                    default:
                      break;
                  }
                  return adIdToCheck === failedAd.id;
                });
              }
            );
            const relaunchFailedAdsForAdsetResult = await relaunchFailedAdsForAdset(
              state,
              campaignFromPackage,
              adsetFromPackage,
              failedPackageAdsFromAdset,
              facebookAdsetId,
              packageLaunchAdSectionComponent,
              updateLaunchingStatusCallback
            );
            return Promise.resolve(relaunchFailedAdsForAdsetResult);
          }
        );
        await Promise.all(relaunchFailedAdsForAdsetPromises);
      } catch (error) {
        console.log('error relaunchFailedAdsInPackage():', error);
      }
    }
  );
  await Promise.all(relaunchAdsForAdsetFromCampaignsPromises);
};

export const relaunchFailedAdsForAdset = async (
  state,
  campaign,
  adset,
  failedAds,
  facebookAdsetId,
  packageLaunchAdSectionComponent,
  updateLaunchingStatusCallback
) => {
  const launchedAdPromises = failedAds.map(async (ad) => {
    const launchAdFromAdsetResult = await launchAdFromAdset(
      state,
      campaign,
      adset,
      ad,
      facebookAdsetId,
      packageLaunchAdSectionComponent,
      updateLaunchingStatusCallback
    );
    return Promise.resolve(launchAdFromAdsetResult);
  });

  const launchedAdResults = await Promise.all(launchedAdPromises);
  return launchedAdResults;
};

export const createTestForm = async (
  packageToLaunch,
  pages,
  token,
  formInState
) => {
  const accountId = '1659209077448923';
  const accountName = 'Nikolai Test';

  const audiencesResult = await getAudiences(accountId, token);

  if (!audiencesResult || audiencesResult.length === 0) return null;

  const audiences = audiencesResult[0].data.concat(audiencesResult[1].data);
  const audienceOptions = _.sortBy(
    _.map(audiences, (op, index) => ({
      key: index,
      value: op.id,
      text: op.name
    })),
    'text'
  );

  // pre-defined values
  const testPageId = '1067945359908634'; // old value: '1708678142759119';
  const testAudienceId = '23843221086480207';
  const testAudienceExclusionId = '23842900611090322';
  const testProductCatalogId = '971505219718291';
  const testProductSetId = '1136531393194688';
  const testInstagramAccount = '485271418180663';
  const destinationUrl = 'http://www.constellationagency.com';

  const conversionAudience = audienceOptions.find(
    (tmp) => tmp.value === testAudienceId
  );
  const conversionExclusion = audienceOptions.find(
    (tmp) => tmp.value === testAudienceExclusionId
  );
  const page = pages.find((item) => item.value.pageId === testPageId);

  const result = await getProductSets(token, testProductCatalogId);
  const productSets = (result ? result.data : []).map((productSet, index) => ({
    key: index,
    text: productSet.name,
    value: productSet.id
  }));

  const productSetOptions = {};
  for (const ad of packageToLaunch.ads) {
    if (ad) {
      productSetOptions[ad.id] = productSets;
    }
  }

  return {
    form: {
      ...formInState,
      accountId,
      accountName,
      pageId: testPageId,
      instagramAccount: testInstagramAccount,
      pixel: '383817388782032',
      startDate: new Date(2019, 11, 1),
      endDate: new Date(2019, 11, 31),
      pageAccessToken: page.value.access_token,
      displayUrl: destinationUrl,
      address: '110 William St, New York, NY 10038',
      radius: '20',
      campaigns: packageToLaunch.campaigns.map((camp) => ({
        id: camp.id,
        spendCap: '100',
        tags: camp.tags
      })),
      adsets: packageToLaunch.adsets.map((adset) => ({
        id: adset.id,
        audiences: [conversionAudience],
        campaignId: adset.campaignId,
        exclusions: [conversionExclusion]
      })),
      ads: packageToLaunch.ads
        .map((ad) => {
          // const { ad: adInAd } = ad || {};
          // const { id } = adInAd || {};
          const { adSetId, type } = ad || {};

          if (!adSetId || !type) return null;

          switch (type.toLowerCase()) {
            case CANVAS_VALUE:
              return {
                id: ad.id,
                destinationUrl,
                adSetId
              };
            case COLLECTION_VALUE:
              return {
                adSetId,
                footerUrl: destinationUrl,
                id: ad.id,
                productCatalog: testProductCatalogId,
                productSet: testProductSetId
              };
            case CAROUSEL_VALUE:
              return {
                adSetId,
                id: ad.id,
                cards: ad.ad.cards.map((card) => {
                  return {
                    cardId: card.id,
                    destinationUrl
                  };
                })
              };
            case CINEMAGRAPH_VALUE:
            case VIDEO_VALUE:
              return {
                id: ad.id,
                destinationUrl,
                adSetId
              };

            case STILL_VALUE:
              return {
                id: ad.id,
                destinationUrl,
                adSetId
              };
            default:
              return null;
          }
        })
        .filter((ad) => ad) // filtering null if there is any
    },
    audienceOptions,
    fillTestData: true,
    productSets // We need to investigate how to set this properly.
  };
};

export const getProductSetOptions = async (catalogId, token) => {
  const result = await getProductSets(token, catalogId);

  const productSets = (result ? result.data : []).map((productSet, index) => ({
    key: index,
    text: productSet.name,
    value: productSet.id
  }));

  const productSetOptions = {};
  for (const ad of this.props.packageToLaunch.ads) {
    productSetOptions[ad.id] = productSets;
  }

  return productSetOptions;
};

export const getLaunchingStatusMessage = (status) => {
  switch (status) {
    case 'Pending':
      return 'Ready to launch...';

    case 'Launching':
      return 'Launching... Please wait until it completes.';
    case 'Succeed':
      return 'Launching completed.';

    case 'Failed':
      return 'Launching failed. Please try again.';

    default:
      return '';
  }
};

export const getIconForLaunchingStatus = (status) => {
  switch (status) {
    case 'Launching':
      return <Icon name="circle notched" loading color="grey" />;

    case 'Succeed':
      return <Icon name="check" color="green" />;

    case 'Failed':
      return <Icon name="close" color="red" />;
    default:
      return null;
  }
};



// WEBPACK FOOTER //
// ./src/screens/PackageBuilder/PackageLaunchActions.js